import React, { useCallback, useEffect, useState } from "react";
import {
  FolderOpen, RefreshCw, ExternalLink, Plus, Trash2, Check, X, Clock,
  ShieldCheck, FileWarning, FlaskConical, Database, Loader2, Cpu, Upload, Users, FileText, Shield,
} from "lucide-react";
import { toast } from "sonner";
import {
  efAdminSubmissions, efAdminSubmission, efAdminAttachSource, efAdminDecide,
  efAdminSettings, efAdminUpdateSettings, efAdminSeedDemo, efAdminListEntities,
  efAdminCreateEntity, efAdminCreateRelationship,
} from "../../lib/api";
import { AdminEpsteinImports } from "./AdminEpsteinImports";
import { AdminEpsteinEntities } from "./AdminEpsteinEntities";
import { AdminEpsteinFragments } from "./AdminEpsteinFragments";
import { AdminEpsteinLifecycle } from "./AdminEpsteinLifecycle";
import { AdminEpsteinSourceReliability } from "./AdminEpsteinSourceReliability";
import { AdminEpsteinIntake } from "./AdminEpsteinIntake";
// TEMPORARY — remove after Batch 1 production import is verified
import { AdminBatch1Panel } from "./AdminBatch1Panel";
// TEMPORARY — Phase 1 cleanup diagnostics; remove after cleanup is verified
import { AdminEpsteinCleanup } from "./AdminEpsteinCleanup";

const STATUS_TABS = ["pending", "held", "approved", "rejected", "all"];

const REL_TYPES = [
  "photographed_with", "appears_in_same_flight_log", "listed_in_flight_logs",
  "listed_in_black_book", "named_in_court_filing", "named_in_deposition",
  "witness_claim_association", "business_association", "property_association",
  "event_attendance", "employment_or_staff_link", "travel_association",
  "communication_or_contact_reference", "financial_link", "other_editor_defined",
];
const KIND_TYPES = ["person","organization","location","document","event","flight","filing","photo","reference","financial"];
const SOURCE_TYPES = ["article","investigative_report","court_filing","deposition","flight_log","black_book","photo","financial_record","witness_testimony","government_record","other"];

export const AdminEpstein = ({ token, userToken = null }) => {
  const [section, setSection] = useState(() => {
    try {
      const s = new URLSearchParams(window.location.search).get("section");
      return ["submissions", "imports", "entities", "fragments"].includes(s) ? s : "submissions";
    } catch { return "submissions"; }
  }); // submissions | imports
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await efAdminSubmissions(token, tab, userToken);
      setRows(r.rows || []); setCounts(r.counts || {});
    } catch (e) { toast.error("Failed to load submissions"); }
    finally { setLoading(false); }
  }, [token, userToken, tab]);
  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    (async () => {
      try {
        setSettings(await efAdminSettings(token, userToken));
      } catch (_e) {
        // settings panel is optional — silent failure is fine
      }
    })();
  }, [token, userToken]);

  const seed = async () => {
    if (!window.confirm("Seed the V1 demo dataset (anchor + 4 entities + 5 relationships + 4 sources, all marked DEMO)?")) return;
    try {
      const r = await efAdminSeedDemo(token, userToken);
      if (r.skipped) toast.info(r.reason);
      else toast.success(`Demo seeded: ${r.entities} entities, ${r.relationships} relationships, ${r.sources} sources.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
  };

  const setDeputyMode = async (mode) => {
    try {
      const s = await efAdminUpdateSettings(token, { ai_deputy_mode: mode }, userToken);
      setSettings(s);
      toast.success(`AI Deputy → ${mode.toUpperCase()}`);
    } catch (e) { toast.error("Failed to update settings"); }
  };

  const toggleArchiveNotice = async () => {
    if (!settings) return;
    const next = !settings.archive_status_notice_enabled;
    try {
      const s = await efAdminUpdateSettings(
        token, { archive_status_notice_enabled: next }, userToken,
      );
      setSettings(s);
      toast.success(`Archive Status Notice → ${next ? "ENABLED" : "DISABLED"}`);
    } catch (e) { toast.error("Failed to update settings"); }
  };

  return (
    <div data-testid="admin-epstein-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <FolderOpen size={13} /> // THE EPSTEIN FILES
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">Living Evidence Desk</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Public submissions, candidate sources, candidate entities, and editor approval.
            Owner has final say — nothing becomes public without an approved source reference.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button onClick={seed} data-testid="ef-admin-seed"
            className="flex items-center gap-2 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Database size={11} /> Seed demo
          </button>
          <button onClick={loadList} data-testid="ef-admin-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Section switcher: Submissions vs Imports */}
      <div className="flex overflow-x-auto whitespace-nowrap gap-1 mb-3 border-b border-[#2D2A26]">
        <button onClick={() => setSection("submissions")} data-testid="ef-admin-section-submissions"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "submissions" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <FolderOpen size={11} className="inline mr-1" /> Submissions
        </button>
        <button onClick={() => setSection("imports")} data-testid="ef-admin-section-imports"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "imports" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <Upload size={11} className="inline mr-1" /> Research import
        </button>
        <button onClick={() => setSection("entities")} data-testid="ef-admin-section-entities"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "entities" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <Users size={11} className="inline mr-1" /> Entities
        </button>
        <button onClick={() => setSection("fragments")} data-testid="ef-admin-section-fragments"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "fragments" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <FileText size={11} className="inline mr-1" /> Fragments
        </button>
        <button onClick={() => setSection("lifecycle")} data-testid="ef-admin-section-lifecycle"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "lifecycle" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <Shield size={11} className="inline mr-1" /> Lifecycle
        </button>
        <button onClick={() => setSection("source-reliability")} data-testid="ef-admin-section-source-reliability"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "source-reliability" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <ShieldCheck size={11} className="inline mr-1" /> Source Reliability
        </button>
        <button onClick={() => setSection("ai-intake")} data-testid="ef-admin-section-ai-intake"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "ai-intake" ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
          <Cpu size={11} className="inline mr-1" /> AI Intake
        </button>
        {/* TEMPORARY — Batch 1 import tab; remove after verified import */}
        <button onClick={() => setSection("batch1")} data-testid="ef-admin-section-batch1"
          className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
            (section === "batch1" ? "border-[#E0A526] text-[#E0A526]" : "border-transparent text-[#E0A526]/60 hover:text-[#E0A526]")}>
          Batch 1 ⚠
        </button>
      </div>

      {section === "entities" && (
        <AdminEpsteinEntities token={token} userToken={userToken} />
      )}

      {section === "fragments" && (
        <AdminEpsteinFragments token={token} userToken={userToken} />
      )}

      {section === "lifecycle" && (
        <AdminEpsteinLifecycle token={token} userToken={userToken} />
      )}

      {section === "source-reliability" && (
        <AdminEpsteinSourceReliability token={token} userToken={userToken} />
      )}

      {section === "ai-intake" && (
        <AdminEpsteinIntake token={token} userToken={userToken} />
      )}

      {/* TEMPORARY — remove this whole block after Batch 1 import is verified */}
      {section === "batch1" && (
        <AdminBatch1Panel token={token} userToken={userToken} />
      )}

      {/* TEMPORARY — Phase 1 cleanup diagnostics; remove after verified */}
      {section === "cleanup" && (
        <AdminEpsteinCleanup token={token} userToken={userToken} />
      )}

      {section === "imports" && (
        <AdminEpsteinImports token={token} userToken={userToken} />
      )}

      {/* AI Deputy + safety strip */}
      {section === "submissions" && settings && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 flex flex-wrap items-center gap-3" data-testid="ef-admin-deputy-strip">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-1">
            <Cpu size={11} /> AI DEPUTY
          </span>
          <div className="flex items-center gap-1">
            {[
              ["off", "OFF"],
              ["assist_only", "ASSIST ONLY"],
              ["auto_draft", "AUTO-DRAFT (V2)"],
              ["limited_auto_approve", "LIMITED AUTO-APPROVE (V2)"],
            ].map(([k, l]) => {
              const active = settings.ai_deputy_mode === k;
              const v2 = k !== "off" && k !== "assist_only";
              return (
                <button key={k} onClick={() => !v2 && setDeputyMode(k)}
                  disabled={v2}
                  data-testid={`ef-admin-deputy-${k}`}
                  className={"px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] border " +
                    (active ? "border-[#D92D20] text-[#D92D20]" : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]") +
                    (v2 ? " opacity-40 cursor-not-allowed" : "")}>
                  {l}
                </button>
              );
            })}
          </div>
          <span className="font-mono text-[10px] text-[#757065] ml-auto">
            V1 modes: OFF / ASSIST_ONLY · V2 wires the LLM triage
          </span>
        </div>
      )}

      {/* Public site — Archive Status Notice toggle */}
      {section === "submissions" && settings && (
        <div
          className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 flex flex-wrap items-center gap-3"
          data-testid="ef-admin-archive-notice-strip"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-1">
            <ShieldCheck size={11} /> ARCHIVE STATUS NOTICE
          </span>
          <button
            type="button"
            onClick={toggleArchiveNotice}
            data-testid="ef-admin-archive-notice-toggle"
            className={
              "px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] border " +
              (settings.archive_status_notice_enabled
                ? "border-[#5DD4B3] text-[#5DD4B3] bg-[#0c1a0e]"
                : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")
            }
          >
            {settings.archive_status_notice_enabled ? "ENABLED" : "DISABLED"}
          </button>
          <span className="font-mono text-[10px] text-[#757065] flex-1 min-w-[200px]">
            When enabled, a full-screen Archive entry notice is shown to public
            visitors. Dismissed per-visitor for 7 days. Use during periods of
            active editorial development.
          </span>
        </div>
      )}

      {section === "submissions" && (
        <>
          <div className="flex overflow-x-auto whitespace-nowrap gap-1 my-3 border-b border-[#2D2A26]">
            {STATUS_TABS.map((t) => {
              const c = t === "all" ? Object.values(counts).reduce((a,b)=>a+b,0) : (counts[t] || 0);
              const active = tab === t;
              return (
                <button key={t} onClick={() => setTab(t)} data-testid={`ef-admin-tab-${t}`}
                  className={"px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
                    (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
                  {t} {c ? <span className="ml-1 text-[#757065]">·&nbsp;{c}</span> : null}
                </button>
              );
            })}
          </div>

          {selectedId ? (
            <SubmissionDetail
              subId={selectedId}
              token={token}
              userToken={userToken}
              onBack={() => { setSelectedId(null); loadList(); }}
            />
          ) : (
            <SubmissionList rows={rows} loading={loading} onPick={setSelectedId} />
          )}
        </>
      )}
    </div>
  );
};

const SubmissionList = ({ rows, loading, onPick }) => {
  if (!loading && rows.length === 0) {
    return (
      <div className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]" data-testid="ef-admin-empty">
        No submissions in this view.
      </div>
    );
  }
  return (
    <div className="border border-[#2D2A26] bg-[#131313]">
      {rows.map((r) => (
        <button key={r.id} onClick={() => onPick(r.id)} data-testid={`ef-admin-row-${r.id}`}
          className="block w-full text-left border-b border-[#2D2A26] px-3 py-3 hover:bg-[#0a0907]">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">{r.status}</span>
            <span className="font-mono text-[9px] text-[#757065]">{r.tracking_id}</span>
            <span className="font-mono text-[9px] text-[#757065] ml-auto">
              {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
            </span>
          </div>
          <div className="text-sm text-[#E4DFD3]">{r.claim?.slice(0, 200)}</div>
          <div className="font-mono text-[10px] text-[#A39E93] mt-1 break-all">{r.source_url}</div>
        </button>
      ))}
    </div>
  );
};

const SubmissionDetail = ({ subId, token, userToken, onBack }) => {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newSrc, setNewSrc] = useState({ title: "", url: "", source_type: "article", excerpt: "", publisher: "" });
  const [linkExisting, setLinkExisting] = useState([]);
  const [newEntities, setNewEntities] = useState([]);
  const [newRels, setNewRels] = useState([]);
  const [editorNote, setEditorNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await efAdminSubmission(token, subId, userToken);
      setSub(r);
    } catch { toast.error("Failed to load submission"); }
    finally { setLoading(false); }
  }, [token, userToken, subId]);
  useEffect(() => { load(); }, [load]);

  const attachSource = async (preset = null) => {
    const payload = preset || newSrc;
    if (!payload.title || !payload.url) { toast.error("Title and URL required."); return; }
    setBusy(true);
    try {
      const r = await efAdminAttachSource(token, subId, payload, userToken);
      toast.success("Source attached");
      setSub((s) => ({ ...s, attached_source_ids: r.attached_source_ids,
                       attached_sources: [...(s.attached_sources || []).filter((x) => x.id !== r.source.id), r.source] }));
      setNewSrc({ title: "", url: "", source_type: "article", excerpt: "", publisher: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Attach failed"); }
    finally { setBusy(false); }
  };

  // Quick-attach helper: take the submission's source_url as a candidate
  const candidateFromSubmission = sub ? {
    title: sub.claim ? sub.claim.slice(0, 100) : "Submitter-provided source",
    url: sub.source_url,
    source_type: ({
      flight_log: "flight_log", court_filing: "court_filing", deposition: "deposition",
      media_report: "article", photo: "photo", black_book: "black_book",
      financial_record: "financial_record", witness_testimony: "witness_testimony",
    })[sub.evidence_type_guess] || "article",
    excerpt: sub.quote_excerpt || "",
    publisher: "",
  } : null;

  const decide = async (decision) => {
    if (decision === "approve") {
      if (!(sub.attached_source_ids || []).length) {
        toast.error("Attach at least one source before approving.");
        return;
      }
    }
    setBusy(true);
    try {
      const r = await efAdminDecide(token, subId, {
        decision, editor_note: editorNote || null,
        attached_source_ids: sub.attached_source_ids || [],
        create_entities: newEntities, link_existing_entity_ids: linkExisting,
        create_relationships: newRels,
      }, userToken);
      if (decision === "approve") {
        toast.success(`Approved — ${r.created_entities?.length || 0} new entities, ${r.created_relationships?.length || 0} new relationships.`);
      } else {
        toast.success(`Marked ${decision}`);
      }
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };

  if (loading || !sub) return <div className="font-mono text-xs text-[#757065]">// LOADING…</div>;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4" data-testid="ef-admin-detail">
      <button onClick={onBack} className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] lg:col-span-2 inline-block text-left">
        ← back to list
      </button>

      <div className="space-y-3">
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">{sub.status}</span>
            <span className="font-mono text-[9px] text-[#757065]">{sub.tracking_id}</span>
            <span className="font-mono text-[9px] text-[#757065] ml-auto">{new Date(sub.created_at).toLocaleString()}</span>
          </div>
          <div className="text-sm text-[#E4DFD3] mb-2 whitespace-pre-wrap">{sub.claim}</div>
          {sub.why_it_matters && <div className="text-xs text-[#A39E93] mb-2">Why it matters: {sub.why_it_matters}</div>}
          {sub.quote_excerpt && (
            <blockquote className="border-l-2 border-[#D92D20] pl-2 italic text-[#CBB892] text-xs">{sub.quote_excerpt}</blockquote>
          )}
          <div className="font-mono text-[10px] text-[#A39E93] mt-2 break-all">
            <a href={sub.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#D92D20]">
              {sub.source_url} <ExternalLink size={9} className="inline" />
            </a>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px] mt-3 text-[#757065]">
            <div>evidence type</div><div className="text-[#E4DFD3]">{sub.evidence_type_guess}</div>
            <div>names</div><div className="text-[#E4DFD3]">{sub.names_referenced || "—"}</div>
            <div>suggested rel</div><div className="text-[#E4DFD3]">{sub.suggested_relationship || "—"}</div>
            <div>contact</div><div className="text-[#E4DFD3]">{sub.contact_email || "—"}</div>
          </div>
        </div>

        {/* Recommended sources */}
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <FlaskConical size={11} /> // RECOMMENDED SOURCES
          </div>
          {candidateFromSubmission && !(sub.attached_source_ids || []).length && (
            <div className="border border-[#9FA8D4]/40 bg-[#0c0e1a] p-2 mb-2" data-testid="ef-admin-candidate-card">
              <div className="font-heading text-sm text-[#E4DFD3]">{candidateFromSubmission.title}</div>
              <div className="font-mono text-[10px] text-[#A39E93] break-all">{candidateFromSubmission.url}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => attachSource(candidateFromSubmission)} disabled={busy}
                  data-testid="ef-admin-attach-candidate"
                  className="bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Attach to file
                </button>
                <a href={candidateFromSubmission.url} target="_blank" rel="noopener noreferrer"
                  className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  Preview
                </a>
              </div>
            </div>
          )}
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">// MANUAL ADD</div>
          <div className="grid gap-2">
            <input value={newSrc.title} onChange={(e) => setNewSrc({ ...newSrc, title: e.target.value })} placeholder="title"
              data-testid="ef-admin-new-source-title" className={inputCls} />
            <input value={newSrc.url} onChange={(e) => setNewSrc({ ...newSrc, url: e.target.value })} placeholder="https://..."
              data-testid="ef-admin-new-source-url" className={inputCls} />
            <select value={newSrc.source_type} onChange={(e) => setNewSrc({ ...newSrc, source_type: e.target.value })}
              data-testid="ef-admin-new-source-type" className={inputCls}>
              {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={newSrc.publisher} onChange={(e) => setNewSrc({ ...newSrc, publisher: e.target.value })} placeholder="publisher (optional)"
              data-testid="ef-admin-new-source-pub" className={inputCls} />
            <textarea rows={2} value={newSrc.excerpt} onChange={(e) => setNewSrc({ ...newSrc, excerpt: e.target.value })}
              placeholder="excerpt (optional)"
              data-testid="ef-admin-new-source-excerpt" className={inputCls} />
            <button onClick={() => attachSource()} disabled={busy || !newSrc.url || !newSrc.title}
              data-testid="ef-admin-attach-manual"
              className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-1">
              <Plus size={11} /> Attach to file
            </button>
          </div>
          {sub.attached_sources?.length > 0 && (
            <div className="mt-3 border-t border-[#2D2A26] pt-2">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1">// ATTACHED</div>
              {sub.attached_sources.map((s) => (
                <div key={s.id} className="border border-[#5DD4B3]/30 bg-[#0a1612] p-2 text-xs mb-1" data-testid={`ef-admin-attached-${s.id}`}>
                  <div className="text-[#E4DFD3]">{s.title}</div>
                  <div className="font-mono text-[10px] text-[#A39E93] break-all">{s.url}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Candidate entities + connections (manual in V1; LLM in V2) */}
        <CandidateEntitiesPanel
          token={token} userToken={userToken}
          existingEntities={sub.all_entities || []}
          linkExisting={linkExisting} setLinkExisting={setLinkExisting}
          newEntities={newEntities} setNewEntities={setNewEntities}
          newRels={newRels} setNewRels={setNewRels}
        />
      </div>

      {/* Decision rail */}
      <div className="space-y-3">
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <FileWarning size={11} /> // CAUTION CHECKLIST
          </div>
          <ul className="text-xs text-[#A39E93] space-y-1">
            <li>· at least one approved source attached?</li>
            <li>· source actually supports the claim?</li>
            <li>· not guilt-by-association only?</li>
            <li>· duplicate of existing evidence?</li>
            <li>· defamation / speculation risk?</li>
            <li>· entity ambiguity resolved?</li>
          </ul>
        </div>

        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <ShieldCheck size={11} /> // OWNER DECISION
          </div>
          <textarea rows={3} value={editorNote} onChange={(e) => setEditorNote(e.target.value)}
            placeholder="editor note (optional)"
            data-testid="ef-admin-editor-note" className={inputCls + " mb-2"} />
          <div className="grid grid-cols-1 gap-2">
            <button onClick={() => decide("approve")} disabled={busy}
              data-testid="ef-admin-approve"
              className="flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
              {busy ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />} Approve &amp; publish
            </button>
            <button onClick={() => decide("hold")} disabled={busy}
              data-testid="ef-admin-hold"
              className="flex items-center justify-center gap-2 border border-[#E0A526]/40 hover:bg-[#1a1408] text-[#E0A526] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <Clock size={11} /> Hold for more research
            </button>
            <button onClick={() => decide("reject")} disabled={busy}
              data-testid="ef-admin-reject"
              className="flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#757065] hover:text-[#D92D20] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <X size={11} /> Reject
            </button>
          </div>
          <div className="text-[10px] text-[#757065] mt-2 italic">
            Approve requires at least one attached source. New entities and relationships materialize on approval and carry the attached sources.
          </div>
        </div>
      </div>
    </div>
  );
};

const CandidateEntitiesPanel = ({ existingEntities, linkExisting, setLinkExisting, newEntities, setNewEntities, newRels, setNewRels }) => {
  const [draft, setDraft] = useState({ name: "", kind: "person", summary: "" });
  const [relDraft, setRelDraft] = useState({ src_entity_id: "", dst_entity_id: "", type: "photographed_with", confidence: "medium", summary: "" });

  const addEntity = () => {
    if (!draft.name) { toast.error("Name required."); return; }
    setNewEntities([...newEntities, { ...draft }]);
    setDraft({ name: "", kind: "person", summary: "" });
  };
  const toggleLink = (id) => {
    setLinkExisting(linkExisting.includes(id) ? linkExisting.filter((x) => x !== id) : [...linkExisting, id]);
  };
  const addRel = () => {
    if (!relDraft.src_entity_id || !relDraft.dst_entity_id) { toast.error("Both endpoints required."); return; }
    setNewRels([...newRels, { ...relDraft }]);
    setRelDraft({ src_entity_id: "", dst_entity_id: "", type: "photographed_with", confidence: "medium", summary: "" });
  };

  // All entity options for the relationship picker = existing + just-staged new ones (give them temp ids)
  const stagedNew = newEntities.map((e, i) => ({ id: `__new__${i}`, name: e.name, kind: e.kind, _staged: true }));
  const allOptions = [...existingEntities, ...stagedNew];

  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// CANDIDATE ENTITIES &amp; CONNECTIONS</div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1">// LINK EXISTING</div>
          <div className="max-h-44 overflow-y-auto border border-[#2D2A26] bg-[#0a0907] p-1 space-y-0.5">
            {existingEntities.map((e) => (
              <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer px-1 py-0.5 hover:bg-[#131313]"
                     data-testid={`ef-admin-link-${e.id}`}>
                <input type="checkbox" checked={linkExisting.includes(e.id)} onChange={() => toggleLink(e.id)} />
                <span className="text-[#E4DFD3]">{e.name}</span>
                <span className="font-mono text-[9px] text-[#757065] ml-auto">{e.kind}</span>
              </label>
            ))}
            {existingEntities.length === 0 && (
              <div className="text-[10px] text-[#757065] italic px-1">No entities yet — create the anchor or seed demo first.</div>
            )}
          </div>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] mb-1">// CREATE NEW ENTITY</div>
          <div className="space-y-1.5">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="name"
              data-testid="ef-admin-new-ent-name" className={inputCls} />
            <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              data-testid="ef-admin-new-ent-kind" className={inputCls}>
              {KIND_TYPES.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <textarea rows={2} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              placeholder="short editor summary" data-testid="ef-admin-new-ent-summary" className={inputCls} />
            <button onClick={addEntity} data-testid="ef-admin-stage-entity"
              className="w-full border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-1">
              <Plus size={10} /> Stage entity for approval
            </button>
          </div>
          {newEntities.length > 0 && (
            <div className="mt-2 space-y-1">
              {newEntities.map((e, i) => (
                <div key={i} className="border border-[#9FA8D4]/30 bg-[#0c0e1a] px-2 py-1 text-xs flex items-center gap-2">
                  <span className="text-[#E4DFD3]">{e.name}</span>
                  <span className="font-mono text-[9px] text-[#757065]">{e.kind}</span>
                  <button onClick={() => setNewEntities(newEntities.filter((_, j) => j !== i))} className="ml-auto text-[#D92D20]">
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Relationship staging */}
      <div className="mt-3 border-t border-[#2D2A26] pt-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#CBB892] mb-1">// STAGE A CONNECTION</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <select value={relDraft.src_entity_id} onChange={(e) => setRelDraft({ ...relDraft, src_entity_id: e.target.value })}
            data-testid="ef-admin-rel-src" className={inputCls}>
            <option value="">— from entity —</option>
            {allOptions.map((e) => <option key={e.id} value={e.id}>{e._staged ? "★ " : ""}{e.name}</option>)}
          </select>
          <select value={relDraft.dst_entity_id} onChange={(e) => setRelDraft({ ...relDraft, dst_entity_id: e.target.value })}
            data-testid="ef-admin-rel-dst" className={inputCls}>
            <option value="">— to entity —</option>
            {allOptions.map((e) => <option key={e.id} value={e.id}>{e._staged ? "★ " : ""}{e.name}</option>)}
          </select>
          <select value={relDraft.type} onChange={(e) => setRelDraft({ ...relDraft, type: e.target.value })}
            data-testid="ef-admin-rel-type" className={inputCls}>
            {REL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={relDraft.confidence} onChange={(e) => setRelDraft({ ...relDraft, confidence: e.target.value })}
            data-testid="ef-admin-rel-conf" className={inputCls}>
            <option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
          </select>
        </div>
        <textarea rows={2} value={relDraft.summary} onChange={(e) => setRelDraft({ ...relDraft, summary: e.target.value })}
          placeholder="editor note for this connection" data-testid="ef-admin-rel-summary" className={inputCls + " mb-2"} />
        <button onClick={addRel} data-testid="ef-admin-stage-rel"
          className="w-full border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-1">
          <Plus size={10} /> Stage connection for approval
        </button>
        {newRels.length > 0 && (
          <div className="mt-2 space-y-1">
            {newRels.map((r, i) => (
              <div key={i} className="border border-[#CBB892]/30 bg-[#1a1408] px-2 py-1 text-xs">
                <span className="font-mono text-[9px] text-[#CBB892] uppercase tracking-[0.25em]">{r.type}</span>
                <button onClick={() => setNewRels(newRels.filter((_, j) => j !== i))} className="ml-2 text-[#D92D20]">
                  <Trash2 size={10} className="inline" />
                </button>
                <div className="text-[#A39E93]">{r.summary || ""}</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-[#757065] mt-2 italic">
          Staged entities/connections only materialize when you press <b className="text-[#E4DFD3]">Approve &amp; publish</b>. They carry the attached sources.
        </p>
      </div>
    </div>
  );
};

const inputCls = "w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#757065]";

export default AdminEpstein;
